import { Outlet, useLocation, NavLink } from 'react-router-dom';
import { Header } from '@/components/header';
import { MobileNav } from '@/components/mobile-nav';
import { Footer } from '@/components/footer';
import { ThemeProvider } from '@/components/theme-provider';
import { UserSync } from '@/components/user-sync';
import { ToastProvider } from '@/components/ui/toaster';
import { I18nProvider } from '@/lib/i18n';
import {
  LayoutDashboard,
  Building2,
  BookOpen,
  Users,
  FileText,
  Video,
  Clapperboard,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const mentorNav = [
  { to: '/mentor', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/mentor/academies', label: 'Academies', icon: Building2 },
  { to: '/mentor/courses', label: 'Courses', icon: BookOpen },
  { to: '/mentor/students', label: 'Students', icon: Users },
  { to: '/mentor/submissions', label: 'Submissions', icon: FileText },
  { to: '/mentor/stream', label: 'Stream', icon: Video },
  { to: '/mentor/recordings', label: 'Recordings', icon: Clapperboard },
  { to: '/mentor/jobs', label: 'Jobs', icon: Briefcase },
];

export function Layout() {
  const location = useLocation();
  const isClassPage = /^\/class\/[^/]+$/.test(location.pathname);
  const isBusinessPlatform =
    location.pathname.startsWith('/projects') || location.pathname.startsWith('/grants');
  const isMentorWorkspace = location.pathname.startsWith('/mentor');

  return (
    <I18nProvider>
      <ThemeProvider>
        <ToastProvider>
          <UserSync />
          <div className="flex min-h-screen flex-col bg-background">
            <Header mode={isBusinessPlatform ? 'business' : 'academy'} />
            {isMentorWorkspace ? (
              <div className="flex flex-1 min-h-0 flex-col pt-14 sm:pt-16">
                <div className="flex flex-1 min-w-0">
                  <aside className="hidden w-56 shrink-0 border-r border-border bg-muted/25 md:flex md:flex-col md:py-8">
                    <nav className="flex flex-col gap-0.5 px-3.5" aria-label="Mentor workspace">
                      {mentorNav.map(({ to, label, icon: Icon, end }) => (
                        <NavLink
                          key={to}
                          to={to}
                          end={end}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                            )
                          }
                        >
                          <Icon className="h-4 w-4 shrink-0 opacity-80" />
                          {label}
                        </NavLink>
                      ))}
                    </nav>
                  </aside>
                  <main className="min-h-[50vh] flex-1 min-w-0 px-4 py-6 sm:px-6 md:px-8 md:py-10 md:pb-12">
                    <Outlet />
                  </main>
                </div>
              </div>
            ) : (
              <main
                className={
                  isClassPage ? 'flex-1 pt-14 sm:pt-16 pb-0' : 'flex-1 pt-14 sm:pt-16 pb-16 md:pb-0'
                }
              >
                <Outlet />
              </main>
            )}
            {!isMentorWorkspace && <MobileNav />}
            {!isMentorWorkspace && <Footer />}
          </div>
        </ToastProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
